/**
 * insuranceModel — pure helpers for the Payer & insurance capture card
 * (FE-1) and the Timeline's insurance-event rendering (FE-3). Binds VERBATIM
 * to the FROZEN BE-3 contract (state/epics/IPD-MVP/round3-wave3/
 * insurance-build-plan.md §API CONTRACT C-1/C-3):
 *
 *   admission row / PATCH body fields — payer_mode ("NONE"|"CASHLESS"|
 *   "REIMBURSEMENT"), insurer_name, policy_no, claim_no, tpa_name (strings),
 *   copay_paise, disallowed_paise (int paise), derived patient_borne_paise =
 *   copay_paise + disallowed_paise.
 *
 * Money is always INTEGER PAISE server-side; this module is the ONE place
 * that converts the rupee strings the inputs hold to/from paise, so the
 * card and the Timeline renderer can't drift on the conversion.
 *
 * TPA-PREAUTH/tpa-bill (S2, FE-1) additions below bind VERBATIM to the
 * FROZEN C1 contract (state/epics/TPA-PREAUTH/tpa-bill/build-plan.md
 * §API CONTRACT C1): `insurer_approved_paise` is now SERVER-DERIVED from
 * `approval_rounds` — this module never lets a caller send it directly
 * (`buildInsurancePatchBody` below no longer emits it at all).
 */

import { PAYER_MODE, PAYER_MODE_LABELS } from '../../constants/admission';
import { formatPaise } from '../../utils/money';

/** rupee input string → integer paise, or null for a blank/invalid entry. */
function toPaiseOrNull(rupeeStr) {
  if (rupeeStr === '' || rupeeStr == null) return null;
  const n = Number(rupeeStr);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.round(n * 100));
}

/**
 * deriveInsuranceForm — initial card state read off the admission row
 * (server denorm; fields are absent/null pre-capture). Amount fields are
 * kept as RUPEE strings (matches the app's existing money-input pattern,
 * e.g. TakeDepositDialog) — '' when unset, never a fake "0".
 */
export function deriveInsuranceForm(admission) {
  const a = admission || {};
  return {
    payer_mode: a.payer_mode || PAYER_MODE.NONE,
    insurer_name: a.insurer_name || '',
    policy_no: a.policy_no || '',
    claim_no: a.claim_no || '',
    tpa_name: a.tpa_name || '',
    copay: a.copay_paise != null ? String(a.copay_paise / 100) : '',
    disallowed: a.disallowed_paise != null ? String(a.disallowed_paise / 100) : '',
  };
}

/**
 * buildInsurancePatchBody — the exact PATCH `/admissions/{id}/insurance`
 * body (C-1) for the current form. `NONE` sends ONLY `payer_mode` — the
 * server clears the insurer/amount fields itself (drop-None→REMOVE), so the
 * FE never has to null every field out one by one. `CASHLESS`/
 * `REIMBURSEMENT` send the claim-identity fields; the two amount fields
 * (copay/disallowed) are sent ONLY for `CASHLESS` (no settlement math
 * applies to a reimbursement claim).
 *
 * NEVER sends `insurer_approved_paise` (TPA-PREAUTH/tpa-bill C1 —
 * `approval_rounds` present ⇒ `insurer_approved_paise` is a 422
 * `APPROVED_IS_DERIVED`; this form no longer carries that field at all, see
 * `ApprovalRoundsList`/`buildApprovalRoundsPatchBody`).
 */
export function buildInsurancePatchBody(form) {
  const f = form || {};
  const mode = f.payer_mode || PAYER_MODE.NONE;
  if (mode === PAYER_MODE.NONE) return { payer_mode: PAYER_MODE.NONE };

  const body = {
    payer_mode: mode,
    insurer_name: (f.insurer_name || '').trim() || null,
    policy_no: (f.policy_no || '').trim() || null,
    claim_no: (f.claim_no || '').trim() || null,
    tpa_name: (f.tpa_name || '').trim() || null,
  };
  if (mode === PAYER_MODE.CASHLESS) {
    body.copay_paise = toPaiseOrNull(f.copay);
    body.disallowed_paise = toPaiseOrNull(f.disallowed);
  }
  return body;
}

// ── Approval rounds (S2, C1 §API CONTRACT) ────────────────────────────────

/** Server-minted id for the read-time-only legacy virtual row (C1, never written). */
export const LEGACY_ROUND_ID = 'legacy-1';

/**
 * deriveApprovalRounds — the admission's `approval_rounds` verbatim,
 * SERVER-ORDERED (`approved_on` DESC, `recorded_at` DESC — newest first).
 * The FE never re-sorts (C1). Tags the legacy virtual row (`round_id ===
 * "legacy-1"`, C1's read-time-only synthesised row) with `isLegacy` so
 * `ApprovalRoundsList` can render it read-only without a second lookup.
 */
export function deriveApprovalRounds(admission) {
  const a = admission || {};
  const rows = Array.isArray(a.approval_rounds) ? a.approval_rounds : [];
  return rows.map((r) => ({ ...r, isLegacy: r.round_id === LEGACY_ROUND_ID }));
}

/**
 * sumApprovalRoundsPaise — Σ `amount_paise` over the given rows. Used ONLY
 * to build the PATCH's `expected_insurer_approved_paise` CAS value (the
 * total the client last read, off whatever it currently renders — the
 * legacy virtual row's `amount_paise` already equals the server's current
 * `insurer_approved_paise`, so summing the rows on screen reproduces it in
 * both the legacy and the real-rows case). NEVER used to render "Approved so
 * far" — that figure is SERVER-COMPUTED (`admission.insurer_approved_paise`,
 * `PayerInsuranceCard`); this is the OPTIMISTIC echo only, the server value
 * always wins on response.
 */
export function sumApprovalRoundsPaise(rounds) {
  return (rounds || []).reduce((sum, r) => sum + (Number(r.amount_paise) || 0), 0);
}

/**
 * buildApprovalRoundsPatchBody — the exact `approval_rounds` +
 * `expected_insurer_approved_paise` pair for the C1 PATCH. FULL-LIST
 * REPLACE: `rounds` must be every row to KEEP (existing rows carry their
 * server `round_id` so the write amends them instead of minting duplicates;
 * a brand-new row omits `round_id` so the server mints one).
 *
 * Orchestrator ruling 2026-09-19 (mockup `tpa-approval-rounds` "Legacy
 * admission" tab: "old single amount becomes one seeded row so history is
 * not lost; new rounds can still be added going forward") — the legacy
 * virtual row (`round_id === LEGACY_ROUND_ID`) is NEVER sent carrying that
 * reserved id (F1/bk-1345 — a client-writable "legacy-1" round_id is a
 * distinct, already-logged bug) but IS materialised into an ORDINARY round
 * input on the first write, if it is still present in `rounds` when the
 * caller saves: `round_id` is stripped (the server mints a fresh one),
 * `note` is stamped `"Approval (recorded before rounds)"`, `reference`
 * stays `null`, and `approved_on` falls back to `legacyFallbackApprovedOn`
 * (the admission's admitted-on date) when the legacy row itself carries no
 * date (BE-1's read-time synthesis never sets one). If the caller has
 * already removed the legacy row from `rounds` before calling this (an
 * EXPLICIT user action, e.g. the Remove control), it is simply absent from
 * the list — no special-casing needed, identical to removing any other
 * round; that save's Σ genuinely drops and the usual decrease/REASON_REQUIRED
 * path applies.
 *
 * `reason` (S3 B-prime, C1's `ApprovalRoundInput.reason`) is carried on a
 * round ONLY when the caller (`ApprovalRoundsList`) has already detected a
 * total decrease and stamped it onto that row's `r.reason` — this function
 * never re-derives the decrease itself (single source: the caller's live
 * total comparison), it just forwards whatever is present, trimmed to
 * `null` when blank so a non-decreasing save never sends a stray field.
 *
 * INTEGRATION (TPA-PREAUTH/tpa-bill, reconcile item (a)): a REMOVAL also
 * lowers the total, but the removed round is by definition no longer in the
 * list — there is NO round left to carry its `reason` (and a full clear
 * sends `[]`, zero carriers). The optional 3rd arg puts that reason at the
 * TOP LEVEL of the body, the only wire slot that can hold it, matching the
 * contract's single per-PATCH `refs.reason` audit field. It is OMITTED
 * entirely when blank, so every save that isn't a post-initiation removal
 * stays byte-identical to what FE-1/FE-3 shipped. NOTE for the backend
 * reconcile: `PatchAdmissionInsuranceRequest` is `extra="forbid"` and does
 * NOT declare `reason` as of BE-1 — BE-3 must add it (see integration.md).
 */
export function buildApprovalRoundsPatchBody(rounds, expectedApprovedPaise, reason, legacyFallbackApprovedOn) {
  const approval_rounds = (rounds || []).map((r) => {
    const isLegacy = r.round_id === LEGACY_ROUND_ID;
    const input = {
      approved_on: isLegacy ? (r.approved_on || legacyFallbackApprovedOn || null) : r.approved_on,
      amount_paise: r.amount_paise,
      reference: isLegacy ? null : ((r.reference || '').trim() || null),
      note: isLegacy ? 'Approval (recorded before rounds)' : ((r.note || '').trim() || null),
      reason: (r.reason || '').trim() || null,
    };
    if (r.round_id && !isLegacy) input.round_id = r.round_id;
    return input;
  });
  const body = { approval_rounds, expected_insurer_approved_paise: expectedApprovedPaise };
  const topLevelReason = (reason || '').trim();
  if (topLevelReason) body.reason = topLevelReason;
  return body;
}

/** Patient-borne = co-pay + disallowed (DERIVED display-only, never a 3rd stored field). */
export function computePatientBornePaise(copayRupeeStr, disallowedRupeeStr) {
  return (toPaiseOrNull(copayRupeeStr) || 0) + (toPaiseOrNull(disallowedRupeeStr) || 0);
}

/**
 * computePatientPayEstimatePaise — the live "patient will pay" preview.
 * The admission's own `balance_paise` is ALREADY net of advance
 * (invoice − advance received, the server's cached truth — see
 * `describeBalance`/`buildAdmissionKpis`), so subtracting the insurer-
 * approved amount from it gives `invoice − advance − insurer-approved`
 * (INV-2) WITHOUT a second fetch or a second advance deduction. Floored at 0
 * — never shown negative (the residual reason lines explain the gap, they
 * don't create a second deduction).
 */
export function computePatientPayEstimatePaise(balancePaise, approvedRupeeStr) {
  const approved = toPaiseOrNull(approvedRupeeStr) || 0;
  return Math.max((Number(balancePaise) || 0) - approved, 0);
}

// ── Timeline — human rendering of ADMISSION_INSURANCE_UPDATED (FE-3) ──────

const MONEY_FIELDS = new Set(['insurer_approved_paise', 'copay_paise', 'disallowed_paise']);
const FIELD_LABELS = {
  payer_mode: 'Claim mode',
  insurer_name: 'Insurer',
  policy_no: 'Policy no.',
  claim_no: 'Pre-auth / claim no.',
  tpa_name: 'TPA',
  insurer_approved_paise: 'Insurer-approved',
  copay_paise: 'Co-pay',
  disallowed_paise: 'Disallowed',
};

function formatChangeValue(field, value) {
  if (value == null || value === '') return '—';
  // BE-3 (C-3) sends money old/new as paise-as-STRING ("300000"); coerce to a
  // Number at the money boundary so it renders as ₹, never the raw string.
  if (MONEY_FIELDS.has(field)) return formatPaise(Number(value));
  if (field === 'payer_mode') return PAYER_MODE_LABELS[value] || value;
  return String(value);
}

/**
 * describeInsuranceEventChanges — turns an `ADMISSION_INSURANCE_UPDATED`
 * event's `refs.changes` (`[{field, old, new}]`, C-3) into short readable
 * "Label: old → new" lines for the Timeline. Returns `[]` when the event
 * carries no changes (contract-silent/absent `refs` never throws).
 */
export function describeInsuranceEventChanges(event) {
  const changes = event && event.refs && event.refs.changes;
  if (!Array.isArray(changes)) return [];
  return changes.map((c) => {
    const label = FIELD_LABELS[c.field] || c.field;
    return `${label}: ${formatChangeValue(c.field, c.old)} → ${formatChangeValue(c.field, c.new)}`;
  });
}
